<template>
  <div class="is-pc customer-drawer-mask" :class="{ 'mobile-drawer': mobile }" @click.self="$emit('close')" @keydown.esc.stop="$emit('close')">
    <section ref="panel" class="customer-drawer" role="dialog" aria-modal="true" aria-labelledby="customer-drawer-title" tabindex="-1" @keydown.tab="trapFocus">
      <header class="drawer-header">
        <div><small>客户档案 · #{{ row.customerId }}</small><h2 id="customer-drawer-title">{{ row.personName || '匿名用户' }}</h2></div>
        <button class="drawer-close" type="button" aria-label="关闭客户详情" @click="$emit('close')">×</button>
      </header>
      <nav class="drawer-tabs" aria-label="详情栏目">
        <button v-for="tab in tabs" :key="tab.value" :class="{ active: activeTab === tab.value }" :aria-pressed="activeTab === tab.value" @click="activeTab = tab.value">{{ tab.label }}</button>
      </nav>
      <div class="drawer-content">
        <screening-review-notice :record="detail || row" />
        <template v-if="activeTab === 'overview'">
          <section class="drawer-section">
            <h3>基本信息</h3>
            <dl class="detail-fields">
              <dt>手机号</dt><dd>{{ row.phone || '—' }}</dd><dt>身份证</dt><dd>{{ maskIdCard(row.idCard) }}</dd>
              <dt>业务员</dt><dd>{{ row.salesmanName || '未分配' }}</dd><dt>客户状态</dt><dd>{{ statusInfo(auditOptions, row.auditStatus).label }}</dd>
              <dt>病种</dt><dd>{{ diseaseText(row.disease) }}</dd><dt>问卷提交</dt><dd>{{ questionnaireText(row) }}</dd>
              <dt>初筛结果</dt><dd>{{ qualifiedText(detail || row) }}</dd>
              <dt>提交时间</dt><dd>{{ formatTime(row.submitTime) }}</dd>
            </dl>
          </section>
          <section class="drawer-section">
            <h3>体检进度</h3>
            <dl class="detail-fields">
              <dt>体检状态</dt><dd>{{ examInfo(row).label }}</dd><dt>体检套餐</dt><dd>{{ row.packageName || '—' }}</dd>
              <dt>城市 / 机构</dt><dd>{{ [row.cityName, row.unitsName].filter(Boolean).join(' / ') || '—' }}</dd>
              <dt>预约时间</dt><dd>{{ reservationText(row) }}</dd><dt>实际到检</dt><dd>{{ row.checkupDate || '—' }}</dd>
              <dt>记录更新</dt><dd>{{ formatTime(row.examUpdatedAt) }}</dd>
            </dl>
            <p class="drawer-note">展示系统已保存的体检进度，不会在打开详情时调用美年接口或创建体检记录。</p>
          </section>
          <section class="drawer-section">
            <h3>体检报告评审</h3>
            <dl class="detail-fields"><dt>评审状态</dt><dd>{{ statusInfo(assessmentOptions, row.assessmentStatus).label }}</dd><dt>评审时间</dt><dd>{{ formatTime(row.reviewedAt) }}</dd></dl>
            <pre class="interpretation">{{ contentText(row.assessmentInterpretation) || '暂无报告评审解读' }}</pre>
          </section>
        </template>
        <template v-else>
          <div v-if="loading" class="drawer-state" role="status">正在加载问卷内容…</div>
          <div v-else-if="error" class="drawer-state" role="alert"><p>{{ error }}</p><button @click="loadQuestionnaire">重新加载</button></div>
          <template v-else-if="detail">
            <section class="drawer-section">
              <h3>{{ detail.questionnaireName || '问卷填写内容' }}</h3>
              <p class="drawer-note">{{ diseaseText(detail.disease) }} · 模板版本 {{ detail.questionnaireVersion || '—' }} · 第 {{ detail.revisionNo || '—' }} 次填写 · {{ formatTime(detail.submitTime) }}</p>
            </section>
            <section v-for="(group, index) in answerGroups" :key="index" class="drawer-section">
              <h3>{{ group.title }}</h3>
              <dl v-for="(answer, answerIndex) in group.items" :key="answerIndex" class="question-answer">
                <dt><small v-if="answer.groupTitle">{{ answer.groupTitle }} / </small>{{ answer.questionTitle }}</dt>
                <dd>{{ answer.displayValue === null || answer.displayValue === undefined || answer.displayValue === '' ? '—' : answer.displayValue }}</dd>
              </dl>
            </section>
            <p v-if="!answerGroups.length" class="drawer-state">暂无问卷填写内容</p>
          </template>
        </template>
      </div>
      <footer class="drawer-footer"><span>{{ mobile ? '身份证已脱敏' : '身份证已脱敏 · Esc 关闭' }}</span><button @click="$emit('close')">返回列表</button></footer>
    </section>
  </div>
</template>

<script>
import { getSanGaoStaffCustomerDetail } from '@/api/sanGaoStaff';
import ScreeningReviewNotice from '../screening-review-notice.vue';
import { AUDIT_OPTIONS, ASSESSMENT_OPTIONS, statusInfo, maskIdCard, formatTime, diseaseText, qualifiedText, questionnaireText, examInfo, reservationText, flagsText, contentText } from './customer-data';

export default {
  name: 'SanGaoCustomerDrawer',
  components: { ScreeningReviewNotice },
  props: { row: { type: Object, required: true }, initialTab: { type: String, default: 'overview' }, mobile: { type: Boolean, default: false } },
  data() {
    return {
      activeTab: this.initialTab, detail: null, loading: false, error: '', requestId: 0,
      auditOptions: AUDIT_OPTIONS, assessmentOptions: ASSESSMENT_OPTIONS,
      tabs: [{ value: 'overview', label: '客户与体检' }, { value: 'questionnaire', label: '问卷内容' }],
    };
  },
  computed: {
    answerGroups() {
      const groups = [];
      ((this.detail && this.detail.answerItems) || []).forEach(item => {
        const title = item.sectionTitle || '问卷填写内容';
        let group = groups.find(entry => entry.title === title);
        if (!group) { group = { title, items: [] }; groups.push(group); }
        group.items.push(item);
      });
      return groups;
    },
  },
  watch: { activeTab(tab) { if (tab === 'questionnaire' && !this.detail && !this.loading) this.loadQuestionnaire(); } },
  mounted() {
    this.previousFocus = document.activeElement;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.$refs.panel.focus();
    if (this.activeTab === 'questionnaire') this.loadQuestionnaire();
  },
  beforeDestroy() {
    this.requestId += 1;
    document.body.style.overflow = this.previousBodyOverflow;
    if (this.previousFocus && document.contains(this.previousFocus)) this.previousFocus.focus();
  },
  methods: {
    statusInfo, maskIdCard, formatTime, diseaseText, qualifiedText, questionnaireText, examInfo, reservationText, flagsText, contentText,
    trapFocus(event) {
      const nodes = this.$refs.panel.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]');
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.$refs.panel)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    },
    async loadQuestionnaire() {
      const requestId = ++this.requestId;
      this.loading = true; this.error = '';
      try {
        const res = await getSanGaoStaffCustomerDetail({ customerId: this.row.customerId });
        if (requestId !== this.requestId) return;
        if (!res || res.code !== 200 || !res.data) throw new Error((res && res.msg) || '问卷加载失败');
        this.detail = res.data;
      } catch (error) {
        if (requestId === this.requestId) this.error = error.message || '网络异常，请重试';
      } finally { if (requestId === this.requestId) this.loading = false; }
    },
  },
};
</script>

<style lang="less" scoped>
.is-pc.customer-drawer-mask { position: fixed; inset: 0; z-index: 2100; background: rgba(22, 43, 65, .35); display: flex; justify-content: flex-end; font-size: 14px; color: #273e54; }
.is-pc .customer-drawer { width: 680px; max-width: 92vw; height: 100%; display: flex; flex-direction: column; background: #fff; box-shadow: -8px 0 30px rgba(20, 45, 70, .12); outline: none; }
.is-pc .drawer-header { padding: 20px 24px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #e8eef4; }
.is-pc .drawer-header small { color: #728397; font-size: 12px; }
.is-pc .drawer-header h2 { margin: 6px 0 0; font-size: 22px; }
.is-pc .drawer-close { border: 0; background: #f1f5f9; font-size: 26px; width: 36px; height: 36px; border-radius: 6px; cursor: pointer; }
.is-pc .drawer-tabs { display: flex; padding: 0 24px; gap: 24px; border-bottom: 1px solid #e8eef4; }
.is-pc .drawer-tabs button { padding: 16px 0; border: 0; border-bottom: 2px solid transparent; background: #fff; font-size: 14px; color: #60758b; cursor: pointer; }
.is-pc .drawer-tabs button.active { border-color: #1677d2; color: #1677d2; font-weight: 600; }
.is-pc .drawer-content { flex: 1; min-height: 0; overflow-y: auto; padding: 0 24px 20px; overscroll-behavior: contain; }
.is-pc .drawer-section { padding: 20px 0; border-bottom: 1px solid #e8eef4; }
.is-pc .drawer-section h3 { margin: 0 0 14px; font-size: 15px; }
.is-pc .detail-fields { display: grid; grid-template-columns: 100px minmax(0, 1fr); gap: 12px; margin: 0; line-height: 1.6; }
.is-pc .detail-fields dt { color: #728397; }
.is-pc .detail-fields dd { margin: 0; overflow-wrap: anywhere; }
.is-pc .drawer-note { color: #728397; font-size: 12px; line-height: 1.8; margin: 14px 0 0; }
.is-pc .interpretation { margin: 16px 0 0; padding: 16px; background: #f5f8fc; border: 1px solid #e7edf5; border-radius: 6px; font-family: inherit; font-size: 14px; line-height: 1.9; white-space: pre-wrap; overflow-wrap: anywhere; }
.is-pc .question-answer { margin: 0; padding: 12px 0; border-bottom: 1px dashed #e8eef4; }
.is-pc .question-answer dt { color: #718196; font-size: 13px; line-height: 1.6; }
.is-pc .question-answer dd { margin: 6px 0 0; line-height: 1.7; white-space: pre-wrap; overflow-wrap: anywhere; }
.is-pc .drawer-state { text-align: center; padding: 48px 16px; color: #718196; }
.is-pc .drawer-footer { display: flex; justify-content: space-between; align-items: center; padding: 16px 24px; border-top: 1px solid #e8eef4; }
.is-pc .drawer-footer span { font-size: 12px; color: #718196; }
.is-pc .drawer-footer button, .is-pc .drawer-state button { border: 1px solid #cddae7; border-radius: 5px; background: #fff; color: #245779; padding: 8px 16px; font-size: 13px; cursor: pointer; }
.is-pc button:focus-visible { outline: 2px solid #1677d2; outline-offset: 2px; }
.is-pc.mobile-drawer .customer-drawer { width: 100%; max-width: none; }
.is-pc.mobile-drawer .drawer-header { padding: calc(12px + env(safe-area-inset-top)) 16px 12px; }
.is-pc.mobile-drawer .drawer-header h2 { font-size: 20px; }
.is-pc.mobile-drawer .drawer-close { width: 44px; height: 44px; }
.is-pc.mobile-drawer .drawer-tabs { gap: 0; justify-content: space-between; padding: 0 16px; }
.is-pc.mobile-drawer .drawer-tabs button { font-size: 13px; min-height: 48px; }
.is-pc.mobile-drawer .drawer-content { padding: 0 16px 16px; -webkit-overflow-scrolling: touch; }
.is-pc.mobile-drawer .detail-fields { grid-template-columns: 84px minmax(0, 1fr); font-size: 13px; gap: 10px; }
.is-pc.mobile-drawer .interpretation { padding: 12px; font-size: 13px; }
.is-pc.mobile-drawer .drawer-footer { padding: 10px 16px calc(10px + env(safe-area-inset-bottom)); }
.is-pc.mobile-drawer .drawer-footer button { min-height: 42px; }
</style>
